"use client";

import { useState } from "react";

import { trackOAuthRedirectStarted } from "@/lib/analytics";
import { getAuthCallbackUrl } from "@/lib/authRedirect";
import { humanizeNetworkOrUnknown } from "@/lib/humanizeClientError";
import { supabase } from "@/lib/supabaseClient";

const ED = {
  font: 'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  text: "#0c4a6e",
  textMuted: "#94a3b8",
  r12: "12px",
};

function IconGoogle() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.42 35.583 29.288 40 24 40c-8.837 0-16-7.163-16-16S15.163 8 24 8c4.065 0 7.746 1.49 10.61 3.928l5.657-5.657C34.046 2.053 29.268 0 24 0 10.745 0 0 10.745 0 24s10.745 24 24 24 24-10.745 24-24c0-1.641-.142-3.25-.41-4.811z" />
      <path fill="#FF3D00" d="M6.305 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c4.065 0 7.746 1.49 10.61 3.928l5.657-5.657C34.046 2.053 29.268 0 24 0 14.795 0 6.683 5.698 2.832 14.691z" />
      <path fill="#4CAF50" d="M24 48c5.162 0 9.86-1.977 13.409-5.192l-6.19-5.238A15.78 15.78 0 0 1 24 40c-5.274 0-9.86-3.392-11.497-8.127l-6.523 5.023C9.506 43.345 16.218 48 24 48z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a15.77 15.77 0 0 1-5.357 7.304l.004-.003 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

const oauthBtn = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.65rem",
  padding: "0.75rem 1rem",
  minHeight: "48px",
  borderRadius: ED.r12,
  fontFamily: ED.font,
  fontWeight: 600,
  fontSize: "0.9rem",
  cursor: "pointer",
  transition: "opacity 0.15s ease, transform 0.12s ease",
};

/**
 * Google — provider verifies email; no separate inbox confirmation for new users on this path.
 * Requires Supabase Dashboard: Google provider enabled and redirect URLs configured.
 */
export function AuthProviderButtons({ onError, disabled = false }) {
  const [busy, setBusy] = useState(/** @type {null | "google"} */ (null));

  function formatOAuthError(err) {
    const raw = err instanceof Error ? err.message : String(err || "");
    const text = raw.toLowerCase();
    const providerName = "Google";

    if (
      text.includes("provider is not enabled") ||
      text.includes("unsupported provider") ||
      text.includes("provider disabled")
    ) {
      return `${providerName} sign-in is not enabled yet. Ask your administrator to enable the ${providerName} provider in Supabase Auth.`;
    }
    if (
      text.includes("redirect_uri_mismatch") ||
      text.includes("redirect uri") ||
      text.includes("invalid_redirect_url")
    ) {
      return `${providerName} sign-in is configured incorrectly (redirect URL mismatch). Ask your administrator to add /auth/callback in Supabase URL configuration.`;
    }
    if (text.includes("access_denied") || text.includes("cancelled")) {
      return `${providerName} sign-in was cancelled. You can try again.`;
    }
    return humanizeNetworkOrUnknown(err) || `Could not start ${providerName} sign-in.`;
  }

  async function startOAuth(provider) {
    if (!supabase || disabled || busy) return;
    onError?.("");
    setBusy(provider);
    try {
      trackOAuthRedirectStarted(provider);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getAuthCallbackUrl("/dashboard"),
          ...(provider === "google"
            ? {
                queryParams: {
                  prompt: "select_account",
                },
              }
            : {}),
        },
      });
      if (error) onError?.(formatOAuthError(error));
    } catch (e) {
      onError?.(formatOAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  const dim = disabled || Boolean(busy);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
      <button
        type="button"
        disabled={dim}
        onClick={() => void startOAuth("google")}
        style={{
          ...oauthBtn,
          border: "1px solid rgba(14, 165, 233, 0.22)",
          background: "rgba(255, 255, 255, 0.98)",
          color: ED.text,
          opacity: dim && !busy ? 0.55 : 1,
          cursor: dim ? "not-allowed" : "pointer",
        }}
      >
        <IconGoogle />
        {busy === "google" ? "Redirecting…" : "Continue with Google"}
      </button>
    </div>
  );
}
